import { useNavigate } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui";
import { hexDeCategoria } from "@/lib/estado-color";
import { useMisEstadisticas } from "../hooks/use-mis-estadisticas";
import type { DistribucionEstadosDto } from "../services/estadisticas-service";

/** Navega a "Mis solicitudes de pago" filtrando por la categoría del segmento. */
type IrACategoria = (seg: Segmento) => void;

/**
 * Donut de "Distribución (últimos 3 meses)" · leyenda a la izquierda + donut
 * grande a la derecha, con el total al centro (= solicitudes CREADAS por el
 * usuario en la ventana de 3 meses).
 *
 * 8 categorías por RESULTADO, mutuamente excluyentes y exhaustivas (cubren todos
 * los estados del enum), así la suma de los pedazos == total. El código no
 * depende solo del color: la leyenda da etiqueta + conteo + PORCENTAJE, y cada
 * arco lleva un `<title>` nativo. SVG puro · sin lib de charts.
 */
export function DistribucionEstadosCard() {
  const { data, isPending } = useMisEstadisticas();
  const navigate = useNavigate();
  // Audiencias con vista GLOBAL (Admin/Auditor/MesaDeControl/Visor) no ven "lo
  // mío" · el segmento de canceladas no es "por mí" sino de cualquier solicitante.
  const esGlobal = data ? data.audiencia !== "Solicitante" && data.audiencia !== "Autorizador" : false;
  const segmentos = esGlobal ? SEGMENTOS_GLOBAL : SEGMENTOS;
  const irACategoria: IrACategoria = (seg) =>
    void navigate({
      to: "/solicitudes",
      search: { categoria: seg.categoria, ...(seg.requiereIncluir ? { incluir: true } : {}) } as never,
    });

  return (
    <div className="flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-4 shadow-2xs">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wide text-foreground">Distribución · últimos 3 meses</h3>
        {!isPending && data ? (
          <span className="text-xs font-bold text-muted-foreground tabular-nums lining-nums">
            {totalDistribucion(data.distribucion)} creadas
          </span>
        ) : null}
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-2">
          <Skeleton height={110} />
          <Skeleton height={176} circle />
        </div>
      ) : (
        <Distribucion distribucion={data?.distribucion} segmentos={segmentos} irACategoria={irACategoria} />
      )}
    </div>
  );
}

interface Segmento {
  key: keyof DistribucionEstadosDto;
  label: string;
  color: string;
  /** Nombre de la categoría (enum del backend) al que filtra este pedazo/leyenda. */
  categoria: string;
  /** true si la categoría está oculta por default en la lista (canceladas/baja). */
  requiereIncluir?: boolean;
}

const SEGMENTOS: ReadonlyArray<Segmento> = [
  { key: "enProceso", label: "En proceso", color: hexDeCategoria("EnProceso"), categoria: "EnProceso" },
  { key: "enAutorizacion", label: "En autorización", color: hexDeCategoria("EnAutorizacion"), categoria: "EnAutorizacion" },
  { key: "autorizadas", label: "Autorizadas", color: hexDeCategoria("Autorizada"), categoria: "Autorizada" },
  { key: "canceladasPorSolicitante", label: "Canceladas por mí", color: hexDeCategoria("Cancelada"), categoria: "Cancelada", requiereIncluir: true },
  { key: "rechazadasPorReglas", label: "Rechazadas por reglas", color: hexDeCategoria("RechazadaPorReglas"), categoria: "RechazadaPorReglas" },
  { key: "rechazadasPorSat", label: "Rechazadas por SAT", color: hexDeCategoria("RechazadaPorSat"), categoria: "RechazadaPorSat" },
  { key: "dadasDeBajaMesaControl", label: "Baja (Mesa de Control)", color: hexDeCategoria("BajaMesaControl"), categoria: "BajaMesaControl", requiereIncluir: true },
  { key: "descartadasPlazoVencido", label: "Descartadas (plazo vencido)", color: hexDeCategoria("DescartadaPlazoVencido"), categoria: "DescartadaPlazoVencido" },
];

/**
 * Variante de las etiquetas para audiencias con vista GLOBAL (Admin/Auditor/
 * MesaDeControl/Visor): "Canceladas por mí" pasa a "Canceladas por el
 * solicitante" (no son del propio viewer). El resto de las etiquetas ya son
 * neutrales, así que solo cambia esa.
 */
const SEGMENTOS_GLOBAL: ReadonlyArray<Segmento> = SEGMENTOS.map((seg) =>
  seg.key === "canceladasPorSolicitante" ? { ...seg, label: "Canceladas por el solicitante" } : seg,
);

function totalDistribucion(d: DistribucionEstadosDto): number {
  return (
    d.enProceso +
    d.enAutorizacion +
    d.autorizadas +
    d.canceladasPorSolicitante +
    d.rechazadasPorReglas +
    d.rechazadasPorSat +
    d.dadasDeBajaMesaControl +
    d.descartadasPlazoVencido
  );
}

function Distribucion({
  distribucion,
  segmentos,
  irACategoria,
}: {
  distribucion: DistribucionEstadosDto | undefined;
  segmentos: ReadonlyArray<Segmento>;
  irACategoria: IrACategoria;
}) {
  if (!distribucion) return <Vacia mensaje="Sin datos" />;
  const total = totalDistribucion(distribucion);
  if (total === 0) return <Vacia mensaje="Sin solicitudes en los últimos 3 meses" />;

  return (
    <div className="my-1 grid flex-1 grid-cols-1 items-center gap-4 sm:grid-cols-2">
      <Leyenda distribucion={distribucion} segmentos={segmentos} total={total} irACategoria={irACategoria} />
      <Donut distribucion={distribucion} segmentos={segmentos} total={total} irACategoria={irACategoria} />
    </div>
  );
}

/** Donut SVG · un `<circle>` por segmento con `stroke-dasharray`. GAP de ~2px. */
function Donut({ distribucion, segmentos, total, irACategoria }: { distribucion: DistribucionEstadosDto; segmentos: ReadonlyArray<Segmento>; total: number; irACategoria: IrACategoria }) {
  const R = 38;
  const C = 2 * Math.PI * R;
  const GAP = 2;

  let offset = 0;
  const arcos = segmentos.map((seg) => {
    const valor = distribucion[seg.key];
    if (valor === 0) return null;
    const len = (valor / total) * C;
    const dash = Math.max(len - GAP, 0.6);
    const pct = Math.round((valor / total) * 100);
    const arco = (
      <circle
        key={seg.key}
        cx="50"
        cy="50"
        r={R}
        fill="none"
        stroke={seg.color}
        strokeWidth="18"
        strokeDasharray={`${dash} ${C - dash}`}
        strokeDashoffset={-offset}
        transform="rotate(-90 50 50)"
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
        onClick={() => irACategoria(seg)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            irACategoria(seg);
          }
        }}
        className="transition-opacity hover:opacity-80"
      >
        <title>{`${seg.label}: ${valor} (${pct}%) · ver en la lista`}</title>
      </circle>
    );
    offset += len;
    return arco;
  });

  return (
    <div className="relative mx-auto h-44 w-44">
      <svg
        viewBox="0 0 100 100"
        className="size-full"
        role="img"
        aria-label={`Distribución por resultado de ${total} solicitudes creadas en los últimos 3 meses`}
      >
        <circle cx="50" cy="50" r={R} fill="none" stroke="var(--color-ink-100)" strokeWidth="18" />
        {arcos}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-extrabold text-ink-900 tabular-nums lining-nums dark:text-ink-100">{total}</span>
        <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Total</span>
      </div>
    </div>
  );
}

function Leyenda({ distribucion, segmentos, total, irACategoria }: { distribucion: DistribucionEstadosDto; segmentos: ReadonlyArray<Segmento>; total: number; irACategoria: IrACategoria }) {
  return (
    <ul className="space-y-0.5 text-xs">
      {segmentos.map((seg) => {
        const valor = distribucion[seg.key];
        const pct = total > 0 ? Math.round((valor / total) * 100) : 0;
        return (
          <li key={seg.key}>
            <button
              type="button"
              onClick={() => irACategoria(seg)}
              disabled={valor === 0}
              title={valor > 0 ? `Ver ${seg.label.toLowerCase()} en la lista` : undefined}
              className="flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: seg.color }} aria-hidden="true" />
                <span className="truncate text-[11px] font-medium text-muted-foreground">{seg.label}</span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="text-[11px] font-semibold text-foreground tabular-nums lining-nums">{valor}</span>
                <span className="w-8 text-right text-[11px] text-muted-foreground tabular-nums lining-nums">{pct}%</span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Vacia({ mensaje }: { mensaje: string }) {
  return (
    <div className="flex h-44 items-center justify-center rounded-md bg-muted">
      <span className="text-xs text-muted-foreground">{mensaje}</span>
    </div>
  );
}
